import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { advisorApi } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import '@/styles/advisor-monitor.css';

type PointsTab = 'overview' | 'rating' | 'rules' | 'manual' | 'services';

type RatingRow = {
  candidate_id: string;
  candidate_name: string;
  year: string;
  rating: number;
  balance: number;
};

type RuleRow = {
  code: string;
  label: string;
  kind: string;
  value: number;
  service_cost: number | null;
};

type LedgerRow = {
  id: string;
  candidate_name: string;
  kind: string;
  points: number;
  reason: string;
  evidence?: string | null;
  created_at?: string;
};

type CandidateOption = {
  id: string;
  full_name: string;
  academia_id: string;
};

const yearLabel = (year: string) => {
  if (year === 'YEAR_1') return '1 курс';
  if (year === 'YEAR_2') return '2 курс';
  if (year === 'YEAR_3') return '3 курс';
  return year;
};

const TABS: Array<{ id: PointsTab; label: string }> = [
  { id: 'overview', label: 'Обзор' },
  { id: 'rating', label: 'Рейтинг' },
  { id: 'rules', label: 'Правила' },
  { id: 'manual', label: 'Ручная правка' },
  { id: 'services', label: 'Услуги (wallet)' },
];

export const AdvisorPointsPage = ({ onClose }: { onClose?: () => void }) => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const close = onClose ?? (() => navigate(routes.advisor));

  const [tab, setTab] = useState<PointsTab>('overview');
  const [loading, setLoading] = useState(true);
  const [rulesCount, setRulesCount] = useState(0);
  const [ledgerCount, setLedgerCount] = useState(0);
  const [ratingPreview, setRatingPreview] = useState<RatingRow[]>([]);
  const [rating, setRating] = useState<RatingRow[]>([]);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [services, setServices] = useState<RuleRow[]>([]);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [candidates, setCandidates] = useState<CandidateOption[]>([]);

  const [candidateId, setCandidateId] = useState('');
  const [points, setPoints] = useState('5');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'overview') {
        const payload = await advisorApi.module('points', { tab: 'overview' });
        setRulesCount(Number(payload.rules_count ?? 0));
        setLedgerCount(Number(payload.ledger_count ?? 0));
        setRatingPreview((payload.rating as RatingRow[] | undefined) ?? []);
      } else if (tab === 'rating') {
        const payload = await advisorApi.module('points', { tab: 'rating' });
        setRating((payload.items as RatingRow[] | undefined) ?? []);
      } else if (tab === 'rules') {
        const payload = await advisorApi.module('points', { tab: 'rules' });
        setRules((payload.items as RuleRow[] | undefined) ?? []);
      } else if (tab === 'services') {
        const payload = await advisorApi.module('points', { tab: 'services' });
        setServices((payload.items as RuleRow[] | undefined) ?? []);
      } else if (tab === 'manual') {
        const payload = await advisorApi.module('points', { tab: 'manual' });
        const list = (payload.candidates as CandidateOption[] | undefined) ?? [];
        setCandidates(list);
        setLedger((payload.ledger as LedgerRow[] | undefined) ?? []);
        if (!candidateId && list[0]) setCandidateId(list[0].id);
      }
    } catch {
      notify('Не удалось загрузить балльную систему', 'error');
    } finally {
      setLoading(false);
    }
  }, [candidateId, notify, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    document.body.classList.add('advisor-monitor-lock');
    return () => document.body.classList.remove('advisor-monitor-lock');
  }, []);

  const onSubmitManual = async (event: FormEvent) => {
    event.preventDefault();
    if (!candidateId || !reason.trim()) {
      notify('Укажите докторанта и причину', 'error');
      return;
    }
    const value = Number(points);
    if (!Number.isFinite(value) || value === 0) {
      notify('Баллы должны быть ненулевым числом', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await advisorApi.manualPoints({
        candidate_id: candidateId,
        points: value,
        reason: reason.trim(),
        evidence: evidence.trim() || undefined,
      });
      notify('Рейтинг обновлён (wallet не изменён)', 'success');
      setReason('');
      setEvidence('');
      await load();
    } catch {
      notify('Не удалось сохранить корректировку', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="advisor-monitor-overlay advisor-monitor-root" role="dialog" aria-modal="true">
      <div className="advisor-monitor-shell">
        <header className="advisor-monitor-top">
          <button type="button" className="advisor-monitor-back" onClick={close} aria-label="Назад">
            ←
          </button>
          <div>
            <small>Баллы докторантов</small>
            <h1>Балльная система докторантов</h1>
            <p>Единый ledger. Rating ≠ Wallet: ручная правка меняет только рейтинг.</p>
          </div>
          <button type="button" className="advisor-monitor-close" onClick={close} aria-label="Закрыть">
            ×
          </button>
        </header>

        <main className="advisor-monitor-main">
          <section className="advisor-monitor-hero" style={{ '--a': '#e2ad3a' } as CSSProperties}>
            <div>
              <small>A05 · RATING ≠ WALLET</small>
              <h2>Контроль начислений и стоимости услуг</h2>
              <p>
                Рейтинг отражает академическую активность. Wallet списывается только через услуги со
                стоимостью. Ручная корректировка не трогает баланс.
              </p>
            </div>
          </section>

          <div className="advisor-monitor-kpis">
            <div className="advisor-monitor-kpi" style={{ '--a': '#e2ad3a' } as CSSProperties}>
              <small>ПРАВИЛА</small>
              <b>{loading && tab === 'overview' ? '—' : rulesCount || rules.length || services.length}</b>
              <span>в каталоге</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#41d2ff' } as CSSProperties}>
              <small>История начислений</small>
              <b>{loading && tab === 'overview' ? '—' : ledgerCount || ledger.length}</b>
              <span>записей</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#49ddb7' } as CSSProperties}>
              <small>РЕЙТИНГ TOP</small>
              <b>{ratingPreview[0]?.rating ?? rating[0]?.rating ?? '—'}</b>
              <span>{ratingPreview[0]?.candidate_name ?? rating[0]?.candidate_name ?? 'лидер'}</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#9e80ff' } as CSSProperties}>
              <small>Баланс</small>
              <b>{ratingPreview[0]?.balance ?? rating[0]?.balance ?? '—'}</b>
              <span>баланс лидера</span>
            </div>
            <div className="advisor-monitor-kpi" style={{ '--a': '#ff7664' } as CSSProperties}>
              <small>УСЛУГИ</small>
              <b>{services.length || '—'}</b>
              <span>со стоимостью</span>
            </div>
          </div>

          <section className="advisor-monitor-panel">
            <header>
              <div>
                <h3>Балльная система</h3>
                <p>Обзор · рейтинг · правила · ручная правка · стоимость услуг</p>
              </div>
            </header>
            <div className="advisor-monitor-body">
              <div className="advisor-monitor-tabs">
                {TABS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={tab === item.id ? 'active' : ''}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="advisor-monitor-empty">Загрузка…</div>
              ) : tab === 'overview' ? (
                <div className="advisor-monitor-table-wrap">
                  <table className="advisor-monitor-table">
                    <thead>
                      <tr>
                        <th>Докторант</th>
                        <th>Курс</th>
                        <th>Rating</th>
                        <th>Wallet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ratingPreview.map((row) => (
                        <tr key={row.candidate_id}>
                          <td>{row.candidate_name}</td>
                          <td>{yearLabel(row.year)}</td>
                          <td>{row.rating}</td>
                          <td>{row.balance}</td>
                        </tr>
                      ))}
                      {!ratingPreview.length ? (
                        <tr>
                          <td colSpan={4}>
                            <div className="advisor-monitor-empty">Нет данных рейтинга</div>
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : tab === 'rating' ? (
                <div className="advisor-monitor-table-wrap">
                  <table className="advisor-monitor-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Докторант</th>
                        <th>Курс</th>
                        <th>Rating</th>
                        <th>Wallet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rating.map((row, index) => (
                        <tr key={row.candidate_id}>
                          <td>{index + 1}</td>
                          <td>{row.candidate_name}</td>
                          <td>{yearLabel(row.year)}</td>
                          <td>{row.rating}</td>
                          <td>{row.balance}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : tab === 'rules' ? (
                <div className="advisor-monitor-table-wrap">
                  <table className="advisor-monitor-table">
                    <thead>
                      <tr>
                        <th>Код</th>
                        <th>Правило</th>
                        <th>Тип</th>
                        <th>Баллы</th>
                        <th>Стоимость услуги</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rules.map((row) => (
                        <tr key={row.code}>
                          <td>{row.code}</td>
                          <td>{row.label}</td>
                          <td>{row.kind}</td>
                          <td>{row.value}</td>
                          <td>{row.service_cost ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : tab === 'services' ? (
                <div className="advisor-monitor-table-wrap">
                  <table className="advisor-monitor-table">
                    <thead>
                      <tr>
                        <th>Код</th>
                        <th>Услуга</th>
                        <th>Списание wallet</th>
                        <th>Rating delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((row) => (
                        <tr key={row.code}>
                          <td>{row.code}</td>
                          <td>{row.label}</td>
                          <td>{row.service_cost}</td>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                      {!services.length ? (
                        <tr>
                          <td colSpan={4}>
                            <div className="advisor-monitor-empty">Нет услуг со стоимостью</div>
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="advisor-monitor-grid">
                  <form
                    className="advisor-monitor-card span6"
                    style={{ '--a': '#e2ad3a' } as CSSProperties}
                    onSubmit={(event) => void onSubmitManual(event)}
                  >
                    <h4>Ручная корректировка рейтинга</h4>
                    <p>Меняет только Rating. Wallet остаётся без изменений.</p>
                    <div className="advisor-monitor-filters" style={{ padding: 0, border: 0, background: 'transparent' }}>
                      <label className="advisor-monitor-field" style={{ gridColumn: '1 / -1' }}>
                        <span>ДОКТОРАНТ</span>
                        <select value={candidateId} onChange={(e) => setCandidateId(e.target.value)}>
                          {candidates.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.full_name} · {item.academia_id}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="advisor-monitor-field">
                        <span>БАЛЛЫ (+/−)</span>
                        <input value={points} onChange={(e) => setPoints(e.target.value)} />
                      </label>
                      <label className="advisor-monitor-field" style={{ gridColumn: 'span 4' }}>
                        <span>ПРИЧИНА</span>
                        <input
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Основание корректировки"
                        />
                      </label>
                      <label className="advisor-monitor-field" style={{ gridColumn: '1 / -1' }}>
                        <span>Подтверждающие материалы</span>
                        <input
                          value={evidence}
                          onChange={(e) => setEvidence(e.target.value)}
                          placeholder="DOI / протокол / ссылка"
                        />
                      </label>
                    </div>
                    <footer>
                      <button type="submit" className="advisor-monitor-btn gold" disabled={submitting}>
                        {submitting ? 'Сохранение…' : 'Применить к Rating'}
                      </button>
                    </footer>
                  </form>

                  <div className="advisor-monitor-card span6" style={{ '--a': '#41d2ff' } as CSSProperties}>
                    <h4>Последние операции ledger</h4>
                    <p>Все виды: MANUAL / EARN / SPEND</p>
                    <div className="advisor-monitor-table-wrap" style={{ marginTop: 10 }}>
                      <table className="advisor-monitor-table" style={{ minWidth: 0 }}>
                        <thead>
                          <tr>
                            <th>Докторант</th>
                            <th>Тип</th>
                            <th>Баллы</th>
                            <th>Причина</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ledger.slice(0, 12).map((row) => (
                            <tr key={row.id}>
                              <td>{row.candidate_name}</td>
                              <td>{row.kind}</td>
                              <td>{row.points}</td>
                              <td>{row.reason}</td>
                            </tr>
                          ))}
                          {!ledger.length ? (
                            <tr>
                              <td colSpan={4}>
                                <div className="advisor-monitor-empty">Ledger пуст</div>
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};
